import { css } from 'aphrodite';
import { useHistory, useLocation, useParams } from 'react-router';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Page } from '../../components-v2/PageLayout';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { AdminSettings } from '../../components/settings/AdminSettings';
import { AdminSettingsSection } from '../../components/settings/models';
import { PersonalSettings } from '../../components/settings/PersonalSettings';
import { ITabbarItem, TabView } from '../../components/TabView';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

type ICategoryMatch = 'admin' | 'personal';

export function Settings() {
	const userSession = useUserSession();
	const history = useHistory();
	const location = useLocation();
	const params = useParams<{ category: ICategoryMatch; action: string }>();

	const isAdmin = Boolean(userSession.user.groups?.find(group => group.toLowerCase() === 'admin'));
	const activeTab = params.category ?? 'personal';
	const onSelectedTabIndexChanged = (_: number, item: ITabbarItem<ICategoryMatch>) => {
		if (item?.dataContext) {
			history.replace(`/settings/${item.dataContext}`);
		}
	};
	const rawSectionToExpand = params.action;
	// For personal settings, keep lowercase. For admin settings, capitalize first letter to match AdminSettingsSection enum values (e.g., "cards" -> "Cards")
	const sectionToExpand = rawSectionToExpand
		? activeTab === 'admin'
			? rawSectionToExpand.charAt(0).toUpperCase() + rawSectionToExpand.slice(1)
			: rawSectionToExpand
		: undefined;

	return (
		<Page>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar
					currentLocationName={location.pathname.includes('schedule-meeting') ? 'Schedule a Meeting' : null}
				/>
			</Portal>
			{isAdmin ? (
				<TabView
					className={css(styleSheet.settingsContainerTabview)}
					onSelectedTabIndexChanged={onSelectedTabIndexChanged}
					selectedTabIndex={activeTab === 'admin' ? 1 : 0}
				>
					{() => {
						return [
							{
								content: <PersonalSettings sectionToExpand={sectionToExpand} />,
								tabbarItem: {
									content: 'Personal',
									dataContext: 'personal',
									onClickRedirectionPath: `/settings`,
								},
							},
							{
								content: <AdminSettings sectionToExpand={(sectionToExpand as AdminSettingsSection) ?? undefined} />,
								tabbarItem: {
									content: 'Admin',
									dataContext: 'admin',
									onClickRedirectionPath: `/settings/admin`,
								},
							},
						];
					}}
				</TabView>
			) : (
				<PersonalSettings sectionToExpand={sectionToExpand} />
			)}
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</Page>
	);
}
